/**
 * Request Signing Utility
 *
 * Signs API requests with timestamp, nonce, and HMAC signature to prevent replay attacks.
 * - Timestamp ensures requests can't be replayed after a short window (30 seconds)
 * - Nonce ensures the same request can't be replayed within that window
 * - Signature ensures the request hasn't been tampered with
 */

import type { SignedRequestHeaders, RequestSigningPayload } from './types';
import { getSynchronizedNow } from './clockOffset';

// Get signing secret from environment
const getSigningSecret = (): string => {
  const secret = import.meta.env.VITE_REQUEST_SIGNING_SECRET;
  if (!secret) {
    console.warn('VITE_REQUEST_SIGNING_SECRET not set. Request signing disabled.');
    return '';
  }
  return secret;
};

/**
 * Generate a cryptographically secure nonce
 */
const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Convert string to ArrayBuffer for crypto operations
 */
const stringToArrayBuffer = (str: string): ArrayBuffer => {
  const encoder = new TextEncoder();
  return encoder.encode(str);
};

/**
 * Convert ArrayBuffer to hex string
 */
const arrayBufferToHex = (buffer: ArrayBuffer): string => {
  const byteArray = new Uint8Array(buffer);
  return Array.from(byteArray, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Generate HMAC-SHA256 signature
 */
const generateSignature = async (payload: string, secret: string): Promise<string> => {
  const key = await crypto.subtle.importKey(
    'raw',
    stringToArrayBuffer(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const signature = await crypto.subtle.sign('HMAC', key, stringToArrayBuffer(payload));
  return arrayBufferToHex(signature);
};

/**
 * Create the payload string for signing
 */
const createPayloadString = (payload: RequestSigningPayload): string => {
  // Create a deterministic string from the payload
  // Format: timestamp|nonce|method|path|bodyHash
  const parts = [
    payload.timestamp.toString(),
    payload.nonce,
    payload.method.toUpperCase(),
    payload.path,
  ];

  // Include body hash if present (for POST/PUT/PATCH)
  if (payload.body) {
    parts.push(payload.body);
  }

  return parts.join('|');
};

/**
 * Sign a request and return the headers to include
 */
export const signRequest = async (
  method: string,
  url: string,
  body?: unknown
): Promise<SignedRequestHeaders | null> => {
  const secret = getSigningSecret();
  if (!secret) {
    return null;
  }

  try {
    // Use the server-corrected clock: a drifting local clock would otherwise
    // make every signed request fail server-side max-age validation.
    const timestamp = getSynchronizedNow();
    const nonce = generateNonce();

    // Extract path from URL
    const urlObj = new URL(url);
    let path = urlObj.pathname + urlObj.search;

    // Supabase edge functions strip /functions/v1/ prefix internally
    // So we need to sign the path as the server will see it
    if (path.startsWith('/functions/v1/')) {
      path = '/' + path.substring('/functions/v1/'.length);
    }

    // Create payload
    const payload: RequestSigningPayload = {
      timestamp,
      nonce,
      method: method.toUpperCase(),
      path,
    };

    // Add body for non-GET requests
    if (body && method.toUpperCase() !== 'GET') {
      payload.body = typeof body === 'string' ? body : JSON.stringify(body);
    }

    // Generate signature
    const payloadString = createPayloadString(payload);
    const signature = await generateSignature(payloadString, secret);

    return {
      'X-Request-Timestamp': timestamp.toString(),
      'X-Request-Nonce': nonce,
      'X-Request-Signature': signature,
    };
  } catch (error) {
    console.error('Error signing request:', error);
    return null;
  }
};

/**
 * Check if request signing is enabled
 */
export const isRequestSigningEnabled = (): boolean => {
  return !!getSigningSecret();
};
